/**
 * PokerVault Auto-Added Variant Guard — client-side prevention of
 * degenerate cart states involving the "special" variants:
 *
 *   - Screensaver-pack upsell (user-triggered via Boost Upsell button)
 *   - Shipping-protection fee (toggled via the cart-summary switch)
 *
 * Runs globally on every page. No-ops when no variant id is seeded on
 * `window.PV`.
 *
 * Two invariants are enforced:
 *
 *   A. qty ≤ 1 for any of the guarded variants.
 *      The theme's purchase-widget lockouts (PDP form hidden, cart-page
 *      qty widget hidden, drawer qty-selector replaced with a locked
 *      "Qty: 1" label) already prevent qty > 1 via the visible UI. This
 *      guard closes the door for anyone calling `/cart/add.js` /
 *      `/cart/change.js` / `/cart/update.js` directly through devtools
 *      or a third-party script.
 *
 *   B. The BONUS-ONLY variant (the upsell) must never be the ONLY item in
 *      the cart. It is a promotional add-on attached to a real purchase;
 *      if the user removes the last "real" line and only the upsell
 *      remains, the cart is effectively orphaned. Clean it out so the
 *      cart shows truly empty.
 *
 *      Shipping protection is INTENTIONALLY EXCLUDED from invariant B —
 *      a customer with only a protection line in their cart is unusual
 *      but legitimate, and silently wiping it would be a UX trap.
 *
 * Strategy:
 *
 *   1. Monkey-patch `window.fetch`. Cart-mutation requests with a JSON
 *      body get any guarded-variant line's `quantity` clamped to 1
 *      before the request is sent.
 *
 *   2. On page load AND on every `cart:update` event, fetch `/cart.js`
 *      (or read the cart from the event) and:
 *        - Clamp any guarded variant with qty > 1 (invariant A).
 *        - If ALL remaining items are BONUS-ONLY variants (invariant B),
 *          POST `/cart/update.js` with qty=0 for each, then re-emit
 *          `cart:update` so the UI refreshes to an empty cart.
 *      Re-entrancy is guarded by a flag so our own follow-up dispatch
 *      doesn't recurse.
 *
 * Backend does NOT enforce qty=1. If somehow the user checks out with N
 * of the upsell, they paid for N and we credit entries for N — no
 * clawback.
 *
 * Vanilla ES5 IIFE, no deps.
 */
(function () {
  'use strict';

  // Variants that must never exceed qty=1 in the cart. The screensaver
  // upsell is user-triggered; the protection fee is toggled from the cart
  // summary. Either could be inflated by a devtools user; this guard
  // normalizes them both (invariant A).
  var GUARDED_VARIANT_IDS = (function () {
    var ids = [];
    var pv = window.PV || {};
    if (typeof pv.upsellVariantId === 'number' && pv.upsellVariantId > 0) ids.push(pv.upsellVariantId);
    if (
      pv.shippingProtection &&
      typeof pv.shippingProtection.variantId === 'number' &&
      pv.shippingProtection.variantId > 0
    ) {
      ids.push(pv.shippingProtection.variantId);
    }
    return ids;
  })();

  // Subset of GUARDED_VARIANT_IDS that drives invariant B (the "all
  // remaining items are orphans → wipe the cart" branch). Excludes the
  // shipping-protection variant intentionally — a cart containing only
  // a protection line is unusual but legitimate, and silently emptying
  // it would be a UX trap.
  var BONUS_ONLY_VARIANT_IDS = (function () {
    var ids = [];
    var pv = window.PV || {};
    if (typeof pv.upsellVariantId === 'number' && pv.upsellVariantId > 0) ids.push(pv.upsellVariantId);
    return ids;
  })();

  if (GUARDED_VARIANT_IDS.length === 0) {
    return;
  }

  function isGuarded(variantId) {
    for (var i = 0; i < GUARDED_VARIANT_IDS.length; i++) {
      if (GUARDED_VARIANT_IDS[i] === variantId) return true;
    }
    return false;
  }

  function isBonusOnly(variantId) {
    for (var i = 0; i < BONUS_ONLY_VARIANT_IDS.length; i++) {
      if (BONUS_ONLY_VARIANT_IDS[i] === variantId) return true;
    }
    return false;
  }

  var CART_MUTATION_RE = /\/cart\/(add|change|update)\.js(\?|$)/;

  /**
   * Normalize a Shopify cart id value to the variant id integer it refers
   * to. Accepts:
   *   - Bare numeric variant id:          `"40979735347249"` or `40979735347249`
   *   - Shopify GID:                      `"gid://shopify/ProductVariant/40979735347249"`
   *   - Cart line key (variant:hash):     `"40979735347249:abc123def"`
   */
  function normalizeVariantId(x) {
    if (x == null) return 0;
    var s = String(x).replace(/^gid:\/\/shopify\/ProductVariant\//, '');
    var prefix = s.split(':')[0]; // strip cart-line hash suffix if present
    var n = parseInt(prefix, 10);
    return isFinite(n) ? n : 0;
  }

  /**
   * Mutate a parsed JSON body in-place, clamping any upsell-variant
   * reference to quantity 1. Returns true if a change was made.
   */
  function clampJsonBody(data) {
    if (!data || typeof data !== 'object') return false;
    var changed = false;

    // /cart/add.js — bulk: `{items: [{id, quantity, properties?}, ...]}`
    if (Array.isArray(data.items)) {
      for (var i = 0; i < data.items.length; i++) {
        var it = data.items[i];
        if (it && typeof it === 'object' && isGuarded(normalizeVariantId(it.id))) {
          if (typeof it.quantity === 'number' && it.quantity > 1) {
            it.quantity = 1;
            changed = true;
          } else if (it.quantity == null) {
            it.quantity = 1; // explicit for safety
            changed = true;
          }
        }
      }
    }

    // /cart/add.js — single: `{id, quantity, properties?}`
    // /cart/change.js — `{id, quantity}` or `{line, quantity}`
    if (
      data.id != null &&
      isGuarded(normalizeVariantId(data.id)) &&
      typeof data.quantity === 'number' &&
      data.quantity > 1
    ) {
      data.quantity = 1;
      changed = true;
    }

    // /cart/update.js — `{updates: {variant_id|line_key: qty, ...}}`
    if (data.updates && typeof data.updates === 'object') {
      for (var key in data.updates) {
        if (!Object.prototype.hasOwnProperty.call(data.updates, key)) continue;
        if (isGuarded(normalizeVariantId(key))) {
          var q = data.updates[key];
          // Shopify accepts numeric or numeric-as-string for the quantity value.
          var qNum = typeof q === 'number' ? q : parseInt(String(q), 10);
          if (qNum > 1) {
            data.updates[key] = 1;
            changed = true;
          }
        }
      }
    }

    return changed;
  }

  /**
   * Rewrite a fetch body if it's a JSON string with an upsell-variant
   * reference. Returns the possibly-mutated body, or the original untouched.
   */
  function rewriteBody(body) {
    if (typeof body !== 'string' || body.length === 0) return body;
    var data;
    try {
      data = JSON.parse(body);
    } catch (_e) {
      return body; // form-encoded / opaque — pass through
    }
    if (clampJsonBody(data)) {
      return JSON.stringify(data);
    }
    return body;
  }

  // ---------------------------------------------------------------------------
  // Monkey-patch window.fetch
  // ---------------------------------------------------------------------------

  if (typeof window.fetch === 'function') {
    var nativeFetch = window.fetch.bind(window);
    window.fetch = function (input, init) {
      try {
        var url = typeof input === 'string' ? input : (input && input.url) || '';
        if (init && init.body && CART_MUTATION_RE.test(url)) {
          var rewritten = rewriteBody(init.body);
          if (rewritten !== init.body) {
            var newInit = {};
            for (var k in init) {
              if (Object.prototype.hasOwnProperty.call(init, k)) newInit[k] = init[k];
            }
            newInit.body = rewritten;
            return nativeFetch(input, newInit);
          }
        }
      } catch (_err) {
        // Never block a cart request on an interceptor bug.
      }
      return nativeFetch(input, init);
    };
  }

  // ---------------------------------------------------------------------------
  // Orphan cleanup — invariant B
  // ---------------------------------------------------------------------------

  var CLEANUP_SOURCE_ID = 'pv-orphan-cleanup';

  /** True if every line in the cart is a bonus-only orphan (the upsell).
   *  Protection lines do NOT count — see invariant B in the module
   *  header. */
  function isAllBonusOnlyOrphans(cart) {
    if (!cart || !Array.isArray(cart.items) || cart.items.length === 0) return false;
    for (var i = 0; i < cart.items.length; i++) {
      if (!isBonusOnly(normalizeVariantId(cart.items[i].variant_id))) return false;
    }
    return true;
  }

  /**
   * Remove all bonus-only orphan lines from the cart (excludes protection
   * even if a stray line is present). Resolves with the updated cart on
   * success, or null on failure / no-op.
   */
  function removeAllBonusOnlyOrphans(cart) {
    if (!cart || !Array.isArray(cart.items) || cart.items.length === 0) {
      return Promise.resolve(null);
    }
    var updates = {};
    var any = false;
    for (var i = 0; i < cart.items.length; i++) {
      var vid = normalizeVariantId(cart.items[i].variant_id);
      if (isBonusOnly(vid)) {
        // `/cart/update.js` accepts variant_id keys; qty 0 removes the line.
        updates[String(cart.items[i].variant_id)] = 0;
        any = true;
      }
    }
    if (!any) return Promise.resolve(null);
    return fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates: updates }),
    }).then(function (r) { return r.ok ? r.json() : null; });
  }

  function dispatchCartUpdate(cart) {
    try {
      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: cart || null,
            sourceId: CLEANUP_SOURCE_ID,
            data: {
              source: CLEANUP_SOURCE_ID,
              itemCount: cart && typeof cart.item_count === 'number' ? cart.item_count : 0,
            },
          },
        })
      );
    } catch (_e) { /* non-fatal */ }
  }

  // Re-entrancy guard. Our cleanup itself dispatches `cart:update` and
  // triggers cart mutations; without this flag the listener would recurse.
  var _cleaningOrphans = false;

  function maybeCleanupOrphans(cart) {
    if (_cleaningOrphans) return;
    if (!isAllBonusOnlyOrphans(cart)) return;
    _cleaningOrphans = true;
    removeAllBonusOnlyOrphans(cart)
      .then(function (updated) {
        if (updated) dispatchCartUpdate(updated);
      })
      .catch(function () { /* non-fatal */ })
      .then(function () { _cleaningOrphans = false; }); // .finally not available pre-ES2018
  }

  function onCartUpdateEvent(e) {
    if (_cleaningOrphans) return;
    if (e && e.detail && e.detail.sourceId === CLEANUP_SOURCE_ID) return;

    var cart = e && e.detail && e.detail.resource;
    if (cart && Array.isArray(cart.items)) {
      maybeCleanupOrphans(cart);
      return;
    }
    // Event didn't carry the cart — fetch and check.
    fetch('/cart.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (c) { if (c) maybeCleanupOrphans(c); })
      .catch(function () { /* non-fatal */ });
  }

  document.addEventListener('cart:update', onCartUpdateEvent);

  // ---------------------------------------------------------------------------
  // Self-heal — runs once on page load. Enforces both invariants A and B.
  // ---------------------------------------------------------------------------

  function selfHeal() {
    // Use our (now-patched) fetch so the response flow is consistent.
    fetch('/cart.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        if (!cart || !Array.isArray(cart.items)) return;

        // Invariant B first: if every line is a bonus-only orphan, purge
        // them all (and skip qty-clamping — the cart is about to be empty
        // anyway). Protection-only carts are exempt — see invariant B in
        // the module header.
        if (isAllBonusOnlyOrphans(cart)) {
          maybeCleanupOrphans(cart);
          return;
        }

        // Invariant A: clamp any guarded line with qty > 1.
        for (var i = 0; i < cart.items.length; i++) {
          var item = cart.items[i];
          var vid = normalizeVariantId(item.variant_id);
          if (isGuarded(vid) && item.quantity > 1) {
            // Our monkey-patched fetch will clamp this too, but we POST
            // explicit qty=1 so the intent is unambiguous in server logs.
            // `id` MUST be a JSON string for /cart/change.js — Shopify
            // 400s on bare integers (only /cart/add.js is lenient).
            (function (clampId) {
              fetch('/cart/change.js', {
                method: 'POST',
                credentials: 'same-origin',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify({ id: String(clampId), quantity: 1 }),
              }).catch(function () { /* non-fatal */ });
            })(vid);
          }
        }
      })
      .catch(function () { /* non-fatal */ });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', selfHeal);
  } else {
    selfHeal();
  }
})();
